import React, { useCallback, useEffect, useState } from 'react';
import { PencilSimple, Plus, SpinnerGap, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const DEFAULT_SCHEDULED_REMINDERS = {
  wishlist: {
    enabled: false,
    days: [],
  },
  candid_photos: {
    enabled: false,
    days: [],
  },
};

const REMINDER_OPTIONS = {
  wishlist: {
    title: 'Wishlist reminders',
    description: 'Send before the event date.',
    directionLabel: 'days before',
    defaultDay: 14,
  },
  candid_photos: {
    title: 'Photo reminders',
    description: 'Send after the event ends.',
    directionLabel: 'days after',
    defaultDay: 1,
  },
};

const normalizeScheduledReminderConfig = (config = {}) => {
  const normalized = {};

  Object.entries(REMINDER_OPTIONS).forEach(([key]) => {
    const defaults = DEFAULT_SCHEDULED_REMINDERS[key];
    const raw = config[key];
    const days = Array.isArray(raw?.days) ? raw.days : defaults.days;
    const uniqueDays = [...new Set(days.map((day) => parseInt(day, 10)).filter((day) => day >= 1 && day <= 365))]
      .sort((a, b) => b - a);

    normalized[key] = {
      enabled: Boolean(raw?.enabled),
      days: uniqueDays,
    };
  });

  return normalized;
};

const formatReminderTiming = (option, day) => {
  const label = day === 1 ? option.directionLabel.replace('days', 'day') : option.directionLabel;
  return `${day} ${label}`;
};

export default function EventScheduledReminderCard({ reminderKey, title, description, compact = false }) {
  const { currentEvent } = useAuth();
  const option = {
    ...REMINDER_OPTIONS[reminderKey],
    title: title || REMINDER_OPTIONS[reminderKey]?.title,
    description: description || REMINDER_OPTIONS[reminderKey]?.description,
  };
  const [scheduledReminders, setScheduledReminders] = useState(DEFAULT_SCHEDULED_REMINDERS);
  const [newReminderDay, setNewReminderDay] = useState(String(option.defaultDay || 1));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const config = scheduledReminders[reminderKey] || DEFAULT_SCHEDULED_REMINDERS[reminderKey];
  const checked = Boolean(config.enabled);

  const fetchWhatsAppConfig = useCallback(async () => {
    if (!currentEvent?.id || !reminderKey) return;
    try {
      setLoading(true);
      const response = await api.get(`/events/${currentEvent.id}/whatsapp/config`);
      setScheduledReminders(normalizeScheduledReminderConfig(response.data?.scheduled_reminders));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load WhatsApp reminder settings');
    } finally {
      setLoading(false);
    }
  }, [currentEvent?.id, reminderKey]);

  useEffect(() => {
    fetchWhatsAppConfig();
  }, [fetchWhatsAppConfig]);

  const saveScheduledReminders = async (next, successMessage) => {
    if (!currentEvent?.id) return;

    const previous = scheduledReminders;
    setScheduledReminders(next);
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('scheduled_reminders', JSON.stringify(next));
      const response = await api.put(`/events/${currentEvent.id}/whatsapp/config`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setScheduledReminders(normalizeScheduledReminderConfig(response.data?.scheduled_reminders || next));
      toast.success(successMessage);
    } catch (error) {
      setScheduledReminders(previous);
      toast.error(error.response?.data?.detail || 'Failed to update scheduled reminder');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleReminder = async (enabled) => {
    const next = normalizeScheduledReminderConfig({
      ...scheduledReminders,
      [reminderKey]: {
        ...config,
        enabled,
      },
    });

    await saveScheduledReminders(next, enabled ? 'Scheduled reminder enabled' : 'Scheduled reminder disabled');
  };

  const handleAddReminderDay = async () => {
    const day = parseInt(newReminderDay, 10);
    if (!day || day < 1 || day > 365) {
      toast.error('Choose a day between 1 and 365');
      return;
    }

    if (config.days.includes(day)) {
      toast.info('That reminder day is already added');
      return;
    }

    const next = normalizeScheduledReminderConfig({
      ...scheduledReminders,
      [reminderKey]: {
        ...config,
        days: [...config.days, day],
      },
    });

    await saveScheduledReminders(next, 'Reminder day added');
  };

  const handleRemoveReminderDay = async (dayToRemove) => {
    const next = normalizeScheduledReminderConfig({
      ...scheduledReminders,
      [reminderKey]: {
        ...config,
        days: config.days.filter((day) => day !== dayToRemove),
      },
    });

    await saveScheduledReminders(next, 'Reminder day removed');
  };

  if (compact) {
    return (
      <Card className="border-border/70 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-wedding-charcoal">{option.title}</p>
                <Badge className={checked ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                  {checked ? 'On' : 'Off'}
                </Badge>
                {saving && <SpinnerGap className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
              <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
                {config.days.length > 0 ? (
                  config.days.map((day) => (
                    <Badge
                      key={`${reminderKey}-${day}`}
                      variant="outline"
                      className="border-muted-foreground/20 bg-muted/30 font-normal text-foreground"
                    >
                      {formatReminderTiming(option, day)}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">{option.description}</span>
                )}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <Switch
                checked={checked}
                disabled={saving || loading || !currentEvent?.id}
                onCheckedChange={handleToggleReminder}
                aria-label={option.title}
                data-testid={`scheduled-whatsapp-${reminderKey}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => setExpanded(!expanded)}
                disabled={loading || !currentEvent?.id}
              >
                <PencilSimple className="mr-1 h-4 w-4" />
                {expanded ? 'Done' : 'Edit'}
              </Button>
            </div>
          </div>

          {expanded && (
            <div className="mt-3 rounded-lg border bg-muted/20 p-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap gap-2">
                  {config.days.length > 0 ? (
                    config.days.map((day) => (
                      <div
                        key={`${reminderKey}-${day}-edit`}
                        className="flex h-8 items-center gap-1 rounded-md border bg-background pl-3 pr-1 text-sm"
                      >
                        <span>{formatReminderTiming(option, day)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                          disabled={saving}
                          onClick={() => handleRemoveReminderDay(day)}
                          aria-label={`Remove ${day} day reminder`}
                        >
                          <Trash className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">Add a timing rule to schedule this reminder.</span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Label htmlFor={`scheduled-day-${reminderKey}`} className="text-xs text-muted-foreground">
                    Add
                  </Label>
                  <Input
                    id={`scheduled-day-${reminderKey}`}
                    type="number"
                    min="1"
                    max="365"
                    step="1"
                    value={newReminderDay}
                    onChange={(event) => setNewReminderDay(event.target.value)}
                    className="h-8 w-20"
                    disabled={saving}
                  />
                  <span className="text-sm text-muted-foreground">{option.directionLabel}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8"
                    disabled={saving}
                    onClick={handleAddReminderDay}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/70 shadow-sm">
      <CardHeader className="px-5 py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>{option.title}</CardTitle>
            <CardDescription className="mt-1">{option.description}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {saving && <SpinnerGap className="h-4 w-4 animate-spin text-muted-foreground" />}
            <Badge className={checked ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
              {checked ? 'On' : 'Off'}
            </Badge>
            <Switch
              checked={checked}
              disabled={saving || loading || !currentEvent?.id}
              onCheckedChange={handleToggleReminder}
              aria-label={option.title}
              data-testid={`scheduled-whatsapp-${reminderKey}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={() => setExpanded(!expanded)}
              disabled={loading || !currentEvent?.id}
            >
              <PencilSimple className="mr-1 h-4 w-4" />
              {expanded ? 'Done' : 'Edit'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        {!currentEvent?.id ? (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            Select an event to manage scheduled reminders.
          </div>
        ) : loading ? (
          <div className="text-sm text-muted-foreground">Loading scheduled reminder...</div>
        ) : (
          <div className="space-y-3">
            <div className="flex min-w-0 flex-wrap gap-2">
              {config.days.length > 0 ? (
                config.days.map((day) => (
                  <Badge
                    key={`${reminderKey}-${day}`}
                    variant="outline"
                    className="border-muted-foreground/20 bg-muted/30 font-normal text-foreground"
                  >
                    {formatReminderTiming(option, day)}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">No timings added</span>
              )}
            </div>

            {expanded && (
              <div className="rounded-lg border bg-muted/20 p-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-wrap gap-2">
                    {config.days.length > 0 ? (
                      config.days.map((day) => (
                        <div
                          key={`${reminderKey}-${day}-edit`}
                          className="flex h-8 items-center gap-1 rounded-md border bg-background pl-3 pr-1 text-sm"
                        >
                          <span>{formatReminderTiming(option, day)}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                            disabled={saving}
                            onClick={() => handleRemoveReminderDay(day)}
                            aria-label={`Remove ${day} day reminder`}
                          >
                            <Trash className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">Add a timing rule to schedule this reminder.</span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Label htmlFor={`scheduled-day-${reminderKey}`} className="text-xs text-muted-foreground">
                      Add
                    </Label>
                    <Input
                      id={`scheduled-day-${reminderKey}`}
                      type="number"
                      min="1"
                      max="365"
                      step="1"
                      value={newReminderDay}
                      onChange={(event) => setNewReminderDay(event.target.value)}
                      className="h-8 w-20"
                      disabled={saving}
                    />
                    <span className="text-sm text-muted-foreground">{option.directionLabel}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8"
                      disabled={saving}
                      onClick={handleAddReminderDay}
                    >
                      <Plus className="mr-1 h-4 w-4" />
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
